import { createSelector } from 'reselect';
import getSrpUrlParams from '@stencil/flights-utils/src/get-srp-url-params';
import isObjectEmpty from '@stencil/flights-utils/src/is-object-empty';
import getBookingReviewLocalStorageData from '@stencil/flights-utils/src/get-booking-review-local-storage-data';
import { __GI_CB__ } from '@stencil/flights-constants/src/urls';
import { getSrpQueryParams } from '../../utils/filterUtils';
import sendDataToNewrelic from '@stencil/send-data-to-new-relic';
import { getPassengersText, getGroundUpRepriceReqDetails } from '../../utils/utils';

export const seoStateSelector = state => state.seo;

export const flavourSelector = createSelector(seoStateSelector, seo => seo.flavour);

export const flightSearchDataSelector = createSelector(seoStateSelector, seo => {
  return {
    source: seo.source,
    destination: seo.destination,
    departure: seo.departure,
    arrival: seo.arrival,
    passengers: seo.passengers,
    flightClass: seo.flightClass,
    journeyType: seo.journeyType,
    travelType: seo.travelType,
    flavour: seo.flavour,
    selectedFareIdentifier: seo.selectedFareIdentifier
  };
});

export const faqsSelector = createSelector(seoStateSelector, seo => seo.faqs);

export const languageSelector = createSelector(seoStateSelector, seo => seo.language);

export const offersDataSelector = createSelector(seoStateSelector, seo => seo.offers);

export const offerOverlaySelector = createSelector(seoStateSelector, seo => seo.offerOverlay);

export const hasSeoDataSelector = createSelector(seoStateSelector, seo => seo.hasSeoData);

export const queryTypeSelector = createSelector(seoStateSelector, seo => seo.queryType);

export const displayOnwardFlightsSelector = createSelector(seoStateSelector, seo =>
  seo.displayOnwardFlights ? seo.displayOnwardFlights : []
);

export const searchWidgetSelector = createSelector(seoStateSelector, seo => seo.searchWidget);

export const searchWidgetSourceSelector = createSelector(
  searchWidgetSelector,
  searchWidget => searchWidget.source
);

export const searchWidgetDestinationSelector = createSelector(
  searchWidgetSelector,
  searchWidget => searchWidget.destination
);

export const searchWidgetDepartureSelector = createSelector(
  searchWidgetSelector,
  searchWidget => searchWidget.departure
);

export const searchWidgetArrivalSelector = createSelector(
  searchWidgetSelector,
  searchWidget => searchWidget.arrival
);

export const searchWidgetJourneyTypeSelector = createSelector(
  searchWidgetSelector,
  searchWidget => searchWidget.journeyType
);

export const searchWidgetPassengersSelector = createSelector(
  searchWidgetSelector,
  searchWidget => searchWidget.passengers
);

export const searchWidgetPassengersTxtSelector = createSelector(
  searchWidgetSelector,
  searchWidgetSelector => {
    const passengers = searchWidgetSelector.passengers;
    return getPassengersText(passengers);
  }
);

export const searchWidgetFlightClassSelector = createSelector(
  searchWidgetSelector,
  searchWidget => searchWidget.flightClass
);

export const searchWidgetTravelTypeSelector = createSelector(
  searchWidgetSelector,
  searchWidget => searchWidget.travelType
);

export const modifySearchWidgetSelector = createSelector(
  seoStateSelector,
  seo => seo.modifySearchWidget
);

export const fareCalendarDataSelector = createSelector(
  seoStateSelector,
  searchWidgetSelector,
  (seo, searchWidget) => {
    const { travelType, arrival, departure } = searchWidget;
    const { fareCalendarData } = seo;
    if (
      travelType === 'international' &&
      arrival &&
      departure &&
      departure.yyyymmdd &&
      fareCalendarData &&
      fareCalendarData.data &&
      fareCalendarData.data[departure.yyyymmdd]
    ) {
      return {
        stats: fareCalendarData.stats,
        data: fareCalendarData.data[departure.yyyymmdd]
      };
    }
    return seo.fareCalendarData;
  }
);

export const fareTrendsDataSelector = createSelector(seoStateSelector, seo => seo.fareTrendsData);
export const travelTypeSelector = createSelector(seoStateSelector, seo => seo.travelType);

export const isFlightSelectedSelector = createSelector(seoStateSelector, seo => {
  const { selectedFlight } = seo;
  return !isObjectEmpty(selectedFlight);
});

export const selectedFlightSelector = createSelector(seoStateSelector, seo => {
  const { selectedFlight } = seo;
  return selectedFlight;
});

export const airportsSelector = createSelector(seoStateSelector, seo => seo.airports);

export const thorDataSelector = createSelector(seoStateSelector, seoState => {
  const {
    onwardFlights,
    returnFlights = [],
    // alternateTimeFlights,
    // alternateLocationFlights,
    selectedFlight,
    source,
    destination,
    departure,
    arrival,
    passengers,
    // domesticRoundTripCombo,
    // domesticRoundTrip,
    travelType,
    airports,
    carriers,
    dates,
    aircrafts,
    // discounts,
    ibp,
    terminalInfo,
    promotionText,
    multiIndex,
    refundability,
    brandIds,
    brandNames,
    discountFares,
    discountNames,
    discountMessages,
    paVal,
    messageValues,
    staticInformation,
    // queryParam,
    isRoundTrip = false
  } = seoState;
  return {
    onwardFlights,
    returnFlights,
    selectedFlight,
    source,
    destination,
    departure,
    arrival,
    passengers,
    travelType,
    airports,
    carriers,
    dates,
    aircrafts,
    ibp,
    terminalInfo,
    promotionText,
    multiIndex,
    refundability,
    brandIds,
    brandNames,
    discountFares,
    discountNames,
    discountMessages,
    paVal,
    messageValues,
    staticInformation,
    isRoundTrip
  };
});

export const bookingReviewDataSelector = createSelector(
  [flightSearchDataSelector, thorDataSelector],
  (flightSearchData, thorData) => {
    if (!thorData.onwardFlights || !thorData.onwardFlights.length) {
      return {};
    }
    const queryParam = getSrpUrlParams(flightSearchData);
    return getBookingReviewLocalStorageData({ queryParam, ...thorData });
  }
);

export const selectedDestinationSelector = createSelector(
  seoStateSelector,
  seo => seo.selectedDestination
);

export const cityCountryBookingReviewDataSelector = createSelector(
  [flightSearchDataSelector, thorDataSelector, selectedDestinationSelector, selectedFlightSelector],
  (flightSearchData, thorData, selectedDestination, selectedFlight) => {
    if (!thorData.onwardFlights) {
      return {};
    }
    const queryParam = getSrpUrlParams({
      ...flightSearchData,
      ...(Object.keys(selectedDestination || {}).length > 0
        ? { destination: { iata: selectedDestination.toCity } }
        : null)
    });
    return getBookingReviewLocalStorageData({ queryParam, ...thorData });
  }
);

export const commonFareRulesDataSelector = createSelector(
  seoStateSelector,
  seo => seo.commonFareRulesData
);

export const commonFareRulesRequestSelector = createSelector(
  [bookingReviewDataSelector, seoStateSelector, flavourSelector],
  (bookingData, seo, flavour) => {
    const {
      onwardFlights,
      returnFlights,
      alternateTimeFlights,
      alternateLocationFlights,
      selectedFlight
    } = seo;
    const bjson = JSON.parse(JSON.stringify(bookingData.bookData));

    bjson.o = onwardFlights[selectedFlight.originalIndex] || [];

    if (selectedFlight.alternateTime) {
      bjson.o = alternateTimeFlights[selectedFlight.originalIndex];
    } else if (selectedFlight.alternateLocation) {
      bjson.o = alternateLocationFlights[selectedFlight.originalIndex];
    }

    bjson.r = returnFlights[selectedFlight.originalIndex] || [];

    const queryParam = bookingData.queryParam.split('-');
    queryParam.pop();

    const param = {
      bjson: JSON.parse(JSON.stringify(bjson)),
      queryParamflavour: flavour,
      qry_data: '#' + queryParam.join('-')
    };

    return param;
  }
);

export const bannerDetailsSelector = createSelector(seoStateSelector, seo => seo.bannerDetails);

export const specialFareOptionsSelector = createSelector(
  seoStateSelector,
  seo => seo.specialFareOptions
);

export const selectedFareIdentifierSelector = createSelector(
  seoStateSelector,
  seo => seo.selectedFareIdentifier
);

export const experimentDetailSelector = createSelector(seoStateSelector, seo => {
  return seo.experimentDetails;
});

export const experimentMetaDataSelector = createSelector(seoStateSelector, seo => {
  return seo.experimentMetaData;
});

export const cityCountryDestSelector = createSelector(seoStateSelector, seo => seo.cityCountryDest);

export const filterSelector = createSelector(seoStateSelector, seo => seo.filterSettings);

export const onwardMaxPriceSelector = createSelector(
  cityCountryDestSelector,
  cityCountryDest => cityCountryDest.onwardMaxPrice
);

export const onwardMinPriceSelector = createSelector(
  cityCountryDestSelector,
  cityCountryDest => cityCountryDest.onwardMinPrice
);

export const onwardDurationSelector = createSelector(
  cityCountryDestSelector,
  cityCountryDest => cityCountryDest.onwardDuration
);

export const cityCountryFamousForSelector = createSelector(
  cityCountryDestSelector,
  cityCountryDest => cityCountryDest?.famousFor
);

export const cityCountryShowCountriesSelector = createSelector(
  cityCountryDestSelector,
  cityCountryDest => cityCountryDest?.showCountries
);

export const sortSelector = createSelector(seoStateSelector, seo => seo.sortSettings);

export const displayDestinationsSelector = createSelector(
  [cityCountryDestSelector],
  cityCountryDest => {
    return cityCountryDest.displayDestinations;
  }
);

export const onwardFilterSelector = createSelector([filterSelector], filter => ({
  onwardStops: filter.onwardStops,
  price: filter.price,
  duration: filter.duration
}));

export const isOnwardFilterAppliedSelector = createSelector(onwardFilterSelector, filter => {
  const filterKeys = Object.keys(filter);

  for (let i = 0; i < filterKeys.length; i++) {
    const filterSetting = filter[filterKeys[i]];
    if (filterSetting && filterSetting.values.length > 0) {
      return true;
    }
  }
  return false;
});

export function filterFunction(list, filters) {
  try {
    const filterKeys = Object.keys(filters);
    const filtered = list.filter(listItem => {
      return filterKeys.every(key => {
        if (filters[key].values.length < 1) return true;
        const flag = filters[key].filterFunc(listItem, filters[key].values);
        return flag;
      });
    });
    return filtered;
  } catch (err) {
    sendDataToNewrelic('FilterFunction', `Failed with error: ${err}`);
  }
}

export const filteredDisplayOnwardDestinations = createSelector(
  [displayDestinationsSelector, filterSelector],
  (destinations, filterSettings) => {
    return filterFunction(destinations, filterSettings);
  }
);

export const sortByPrice = {
  asc: function (a, b) {
    return a.minCostStop.price - b.minCostStop.price;
  },
  des: function (a, b) {
    return b.minCostStop.price - a.minCostStop.price;
  }
};

export const sortByDuration = {
  asc: function (a, b) {
    return (
      parseInt(a.minCostStop.onwardFlightDurationInMins) +
      parseInt(a.minCostStop?.returnFlightDurationInMins || '0') -
      (parseInt(b.minCostStop.onwardFlightDurationInMins) +
        parseInt(b.minCostStop?.returnFlightDurationInMins || '0'))
    );
  },
  des: function (a, b) {
    return (
      parseInt(b.minCostStop.onwardFlightDurationInMins) +
      parseInt(b.minCostStop?.returnFlightDurationInMins || '0') -
      (parseInt(a.minCostStop.onwardFlightDurationInMins) +
        parseInt(a.minCostStop?.returnFlightDurationInMins || '0'))
    );
  }
};

export const sortByFlightPrice = {
  asc: function (a, b) {
    if (a.flightArr.length > 1 || b.flightArr.length > 1) {
      if (a.valuesForSort.cost === b.valuesForSort.cost) {
        if (a.valuesForSort.duration === b.valuesForSort.duration) {
          return a.valuesForSort.departure - b.valuesForSort.departure;
        }
        return a.valuesForSort.duration - b.valuesForSort.duration;
      }
      return a.valuesForSort.cost - b.valuesForSort.cost;
    } else {
      if (a.valuesForSort.cost === b.valuesForSort.cost) {
        if (a.valuesForSort.departure === b.valuesForSort.departure) {
          return a.valuesForSort.duration - b.valuesForSort.duration;
        }
        return a.valuesForSort.departure - b.valuesForSort.departure;
      }
      return a.valuesForSort.cost - b.valuesForSort.cost;
    }
  },
  des: function (a, b) {
    return b.valuesForSort.cost - a.valuesForSort.cost;
  }
};

export const sortByFlightDuration = {
  asc: function (a, b) {
    return a.valuesForSort.duration - b.valuesForSort.duration;
  },
  des: function (a, b) {
    return b.valuesForSort.duration - a.valuesForSort.duration;
  }
};

const sortFunctionDict = {
  cheapest: sortByPrice,
  shortest: sortByDuration
};

const sortFlightsFunctionDict = {
  cheapest: sortByFlightPrice,
  shortest: sortByFlightDuration
};

function sortList(sortFunction, list) {
  if (!list) {
    return [];
  }
  return list.sort(sortFunction);
}

export function sortFunction(listToBeSorted, sortSettings, sortFlights = false) {
  try {
    if (sortFlights) {
      // Sort flights
      return sortList(
        sortFlightsFunctionDict[sortSettings.currentSort][sortSettings.isAscending ? 'asc' : 'des'],
        listToBeSorted
      );
    } else {
      // Sort destinations
      return sortList(
        sortFunctionDict[sortSettings.currentSort][sortSettings.isAscending ? 'asc' : 'des'],
        listToBeSorted
      );
    }
  } catch (err) {
    console.log('Error ', err);
    // sendDataToNewrelic(`SortFunction - ${sortSettings.currentSort}`, `Failed with error: ${err}`);
  }
}

export const filteredAndSortedDisplayOnwardDestinations = createSelector(
  [filteredDisplayOnwardDestinations, sortSelector],
  (filteredDisplayOnwardDestinations, sortSettings) => {
    try {
      if (
        filteredDisplayOnwardDestinations &&
        filteredDisplayOnwardDestinations[filteredDisplayOnwardDestinations.length - 1] &&
        filteredDisplayOnwardDestinations[filteredDisplayOnwardDestinations.length - 1]
          .originalIndex === 'viewAllFlights'
      ) {
        filteredDisplayOnwardDestinations.pop();
      }
      let sortedList = sortFunction(filteredDisplayOnwardDestinations, sortSettings);
      let updateSortedList = [];
      let listOfCities = [];
      if (sortedList) {
        for (let i = 0; i < sortedList.length; i++) {
          if (!listOfCities.includes(sortedList[i]?.toCity)) {
            listOfCities.push(sortedList[i]?.toCity);
            updateSortedList.push({ ...sortedList[i] });
          }
        }
      }
      if (updateSortedList.length > 0) {
        sortedList = [...updateSortedList];
      }
      return sortedList;
    } catch (err) {
      console.log('err', err);
      // sendDataToNewrelic('filteredAndSortedDisplayOnwardFlights', `Failed with error: ${err}`);
      return filteredDisplayOnwardDestinations;
    }
  }
);

export const sortFlightsSelector = createSelector(seoStateSelector, seo => seo.sortFlightSettings);

export const filteredAndSortedDisplayDetailsFlights = createSelector(
  [displayOnwardFlightsSelector, sortFlightsSelector],
  (displayOnwardFlights, sortFlightsSettings) => {
    try {
      if (
        displayOnwardFlights &&
        displayOnwardFlights[displayOnwardFlights.length - 1] &&
        displayOnwardFlights[displayOnwardFlights.length - 1].originalIndex === 'viewAllFlights'
      ) {
        displayOnwardFlights.pop();
      }
      let sortedList = sortFunction(displayOnwardFlights, sortFlightsSettings, true);

      return sortedList;
    } catch (err) {
      console.log('err', err);
      // sendDataToNewrelic('filteredAndSortedDisplayOnwardFlights', `Failed with error: ${err}`);
      return displayOnwardFlights;
    }
  }
);

export const tripDurationSelector = createSelector(seoStateSelector, seo => {
  return seo.searchWidget.tripDuration;
});

export const constructThorUrlForCityCountry = createSelector(
  [flightSearchDataSelector, selectedDestinationSelector, flavourSelector],
  (flightSearchData, selectedDestination, flavour) => {
    const { travelType, departure, arrival } = flightSearchData;
    if (
      !selectedDestination ||
      !selectedDestination.toCity ||
      (arrival && departure && arrival.dateObj < departure.dateObj)
    ) {
      return null;
    }

    const query = `air-${getSrpQueryParams(
      {
        ...flightSearchData,
        destination: { iata: selectedDestination.toCity },
        departure,
        arrival
      },
      true
    )}`;

    return `${__GI_CB__}/v2/thor/rest/flight/search/${
      travelType === 'international' ? 'int' : ''
    }?application=fbs&flavour=${flavour}&mime=html&script=y&actionData=[{%22query%22:%22${query}%22}]&slotfl=y1&nearbyfl=y1`;
  }
);

export const prefetchedChunksSelector = createSelector(seoStateSelector, seo => seo.chunks);

export const hasPrefetchedChunksSelector = createSelector(prefetchedChunksSelector, chunks => {
  return chunks.allChunks;
});

export const isStoreEmptySelector = createSelector(seoStateSelector, seo => seo.isStoreEmpty);

// thor
export const dataIndexSelector = createSelector(seoStateSelector, seo => seo.dataIndex);

export const unorderedChunksSelector = createSelector(seoStateSelector, seo => seo.unorderedChunks);

export const unorderedChunksLengthSelector = createSelector(
  seoStateSelector,
  seo => Object.keys(seo.unorderedChunks).length
);

export const onwardFlightsCurrentLengthSelector = createSelector(
  displayOnwardFlightsSelector,
  displayOnwardFlights => {
    return displayOnwardFlights ? displayOnwardFlights.length : 0;
  }
);

export const returnFlightsCurrentLengthSelector = createSelector(
  seoStateSelector,
  seo => seo.returnFlights.length
);

export const displayAlternateTimeFlightsSelector = createSelector(
  seoStateSelector,
  seo => seo.displayAlternateTimeFlights
);

export const displayAlternateTimeFlightsLengthSelector = createSelector(
  displayAlternateTimeFlightsSelector,
  flights => flights.length
);

export const displayAlternateLocationFlightsSelector = createSelector(
  seoStateSelector,
  seo => seo.displayAlternateLocationFlights
);

export const displayAlternateLocationFlightsLengthSelector = createSelector(
  displayAlternateLocationFlightsSelector,
  flights => flights.length
);

export const filterMinMaxSelector = createSelector(seoStateSelector, seo => seo.filterMinMaxValues);
export const showOnePaxPriceSelector = createSelector(seoStateSelector, seo => seo.showOnePaxPrice);

export const sourceSelector = createSelector(seoStateSelector, seo => seo.source);
export const destinationSelector = createSelector(seoStateSelector, seo => seo.destination);
export const departureSelector = createSelector(seoStateSelector, seo => seo.departure);
export const arrivalSelector = createSelector(seoStateSelector, seo => seo.arrival);
export const passengerSelector = createSelector(seoStateSelector, seo => seo.passengers);

export const onwardFlightsSelector = createSelector(seoStateSelector, seo => seo.onwardFlights);
export const returnFlightsSelector = createSelector(seoStateSelector, seo => seo.returnFlights);

export const displayInfoSelector = createSelector(seoStateSelector, seo => ({
  refundability: seo.refundability,
  airports: seo.airports,
  carriers: seo.carriers,
  dates: seo.dates,
  messageText: seo.messageText,
  messageValues: seo.messageValues,
  messageIcons: seo.messageIcons,
  discountFares: seo.discountFares,
  discountMessages: seo.discountMessages,
  showOnePaxPrice: seo.showOnePaxPrice
}));

export const totalPaxSelector = createSelector(seoStateSelector, seo => {
  const { passengers } = seo;
  return passengers.adult + passengers.child + passengers.infant;
});

export const domesticRoundTripDiscountSelector = createSelector(
  seoStateSelector,
  seo => seo.domesticRoundTripDiscounts
);

export const journeyTypeSelector = createSelector(seoStateSelector, seo => seo.journeyType);
export const directlyLandedSelector = createSelector(seoStateSelector, seo => seo.directlyLanded);
export const staticInformationSelector = createSelector(
  seoStateSelector,
  seo => seo.staticInformation
);

export const correlationIdForLoggingSelector = createSelector(
  seoStateSelector,
  seo => seo?.correlationIdForLogging
);

export const queryBRPSelector = createSelector(
  seoStateSelector,
  seo => seo?.thorGroundUpData?.query
);

export const preSearchBannerDetailsSelector = createSelector(
  seoStateSelector,
  seo => seo?.preSearchBannerDetails?.banners || []
);

export const groundUpOnwardFlightsSelector = createSelector(
  seoStateSelector,
  seo => seo?.thorGroundUpData?.recommendation?.[0] || []
);

export const groundUpOnwardFlightsWithSpecialCardsSelector = createSelector(
  [groundUpOnwardFlightsSelector, flavourSelector, preSearchBannerDetailsSelector],
  (list, flavour, bannerDetails) => {
    const clonedList = [...list];
    if (flavour === 'PWA' && clonedList.length) {
      if (Array.isArray(bannerDetails)) {
        let isSpilledOverBannerIncluded = false;
        bannerDetails.forEach(bannerData => {
          const { type, position } = bannerData;
          if (type?.toLowerCase() === 'inline' && position > 0 && list.length) {
            //only 1 banner can spillover
            if (position > list.length) {
              if (!isSpilledOverBannerIncluded) {
                clonedList.splice(clonedList.length, 0, {
                  specialCard: 'inlineBanner',
                  data: bannerData
                });
                isSpilledOverBannerIncluded = true;
              }
            } else {
              clonedList.splice(position, 0, {
                specialCard: 'inlineBanner',
                data: bannerData
              });
            }
          } else if (type?.toLowerCase() === 'top') {
            clonedList.splice(0, 0, { specialCard: 'specialFaresBanner', data: bannerData });
          }
        });
      }
    }
    return clonedList;
  }
);

export const groundUpPostSearchRkeysDataSelector = createSelector(
  seoStateSelector,
  seo => seo?.thorGroundUpData?.ps?.rKeyData || {}
);

export const groundUpDisplayValuesSelector = createSelector(
  seoStateSelector,
  seo => seo?.thorGroundUpData?.dv || []
);

export const selectedBannerDetailsSelector = createSelector(
  seoStateSelector,
  seo => seo?.selectedBannerIndex || { bannerPositionIndex: null, horizontalIndex: null }
);

export const deviceIdSelector = createSelector(seoStateSelector, seo => seo.uuid || '');

export const selectedPromoCodeSelector = createSelector(
  [selectedBannerDetailsSelector, preSearchBannerDetailsSelector],
  (selectedBannerDetails, bannersData) => {
    if (
      selectedBannerDetails.bannerPositionIndex !== null &&
      selectedBannerDetails.horizontalIndex !== null &&
      Array.isArray(bannersData)
    ) {
      const selectedBanner = bannersData.find(
        banner => banner?.position === selectedBannerDetails.bannerPositionIndex
      );
      const promo_code = selectedBanner?.data?.[selectedBannerDetails.horizontalIndex]?.promo_code;
      if (promo_code) {
        return { promo: promo_code };
      }
    }
    return null;
  }
);

export const repriceRequestGroundupSelector = createSelector(
  [
    selectedFlightSelector,
    flavourSelector,
    experimentMetaDataSelector,
    queryBRPSelector,
    selectedPromoCodeSelector
  ],
  (onwardSelectedFlight, flavour, experimentMetaData, queryBrp, promoCode) => {
    if (
      !onwardSelectedFlight ||
      (typeof onwardSelectedFlight === 'object' && !Object.keys(onwardSelectedFlight).length)
    ) {
      return null;
    }
    const { v, rid, rcoms } = getGroundUpRepriceReqDetails(
      queryBrp,
      experimentMetaData,
      onwardSelectedFlight
    );
    return {
      flavour,
      v: v ? 1.6 : 1.5,
      rid,
      rcoms: JSON.stringify(rcoms),
      ...(promoCode && { applied_addons: JSON.stringify(promoCode) })
    };
  }
);

export const bookingReviewGroundUpDataSelector = createSelector(
  [
    flightSearchDataSelector,
    thorDataSelector,
    groundUpOnwardFlightsSelector,
    selectedFareIdentifierSelector
  ],
  (flightSearchData, thorData, onwardFilghts, selectedFareIdentifier) => {
    if (!onwardFilghts?.length) {
      return {};
    }
    const queryParam = getSrpUrlParams(flightSearchData, true, selectedFareIdentifier);
    return getBookingReviewLocalStorageData({ queryParam, ...thorData });
  }
);

export const isGroundupSelector = createSelector(seoStateSelector, seoState => seoState.isGroundup);

export const seoOmnitureInfoSelector = createSelector(
  [seoStateSelector, isGroundupSelector],
  (seo, isGroundup) => {
    if (
      typeof seo?.seoOmnitureInfo === 'object' &&
      seo.seoOmnitureInfo.pageLoad &&
      Object.keys(seo.seoOmnitureInfo.pageLoad).length &&
      isGroundup
    ) {
      return {
        ...seo.seoOmnitureInfo,
        pageLoad: {
          ...seo.seoOmnitureInfo.pageLoad,
          page: {
            ...(seo.seoOmnitureInfo.pageLoad?.page || null),
            pageInfo: {
              ...(seo.seoOmnitureInfo.pageLoad?.page?.pageInfo || null),
              abVariant: 'Mweb Groundup'
            }
          }
        }
      };
    }
    return seo?.seoOmnitureInfo;
  }
);

export const messageValuesSelector = createSelector(seoStateSelector, srp => srp.messageValues);

export const postSearchRkeysDataSelector = createSelector(
  seoStateSelector,
  srp => srp?.postSearchData?.rKeyData || {}
);
